import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './Contact.css';

const Contact = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setSubmitStatus('success');
      setIsSubmitting(false);
      setFormData({ name: '', email: '', subject: '', message: '' });
      
      setTimeout(() => setSubmitStatus(''), 5000);
    }, 2000);
  };

  const contactInfo = [
    {
      icon: 'fas fa-envelope',
      title: 'Email',
      value: 'alex.johnson@email.com',
      link: 'mailto:alex.johnson@email.com'
    },
    {
      icon: 'fas fa-phone',
      title: 'Phone',
      value: '+1 (555) 123-4567',
      link: 'tel:+15551234567'
    },
    {
      icon: 'fas fa-map-marker-alt',
      title: 'Location',
      value: 'San Francisco, CA',
      link: 'https://maps.google.com/?q=San Francisco, CA'
    }
  ];

  const socialLinks = [
    {
      icon: 'fab fa-linkedin',
      name: 'LinkedIn',
      url: 'https://linkedin.com/in/alexjohnson',
      color: '#0077b5'
    },
    {
      icon: 'fab fa-github',
      name: 'GitHub',
      url: 'https://github.com/alexjohnson',
      color: '#333'
    },
    {
      icon: 'fab fa-twitter',
      name: 'Twitter',
      url: 'https://twitter.com/alexjohnson',
      color: '#1da1f2'
    },
    {
      icon: 'fab fa-instagram',
      name: 'Instagram',
      url: 'https://instagram.com/alexjohnson',
      color: '#e4405f'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="contact section" id="contact" ref={ref}>
      <div className="container">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.div className="section__header" variants={itemVariants}>
            <h2 className="section__title">Get In Touch</h2>
            <p className="section__subtitle">Let's work together on your next project</p>
          </motion.div>

          <div className="contact__content">
            <motion.div className="contact__info" variants={itemVariants}>
              <div className="contact__intro">
                <h3 className="contact__title">Let's discuss your project</h3>
                <p className="contact__description">
                  I'm always interested in new opportunities and exciting projects. 
                  Whether you have a question, want to collaborate, or just want to say hello, 
                  I'd love to hear from you!
                </p>
              </div>

              <div className="contact__details">
                {contactInfo.map((item, index) => (
                  <motion.div
                    key={item.title}
                    className="contact__detail"
                    variants={itemVariants}
                    whileHover={{ scale: 1.05, x: 10 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="contact__detail-icon">
                      <i className={item.icon}></i>
                    </div>
                    <div className="contact__detail-content">
                      <h4 className="contact__detail-title">{item.title}</h4>
                      <a 
                        href={item.link}
                        className="contact__detail-value"
                        target={item.link.startsWith('http') ? '_blank' : '_self'}
                        rel={item.link.startsWith('http') ? 'noopener noreferrer' : ''}
                      >
                        {item.value}
                      </a>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="contact__social">
                <h4 className="contact__social-title">Follow me on social media</h4>
                <div className="contact__social-links">
                  {socialLinks.map((social, index) => (
                    <motion.a
                      key={social.name}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="contact__social-link"
                      style={{ '--social-color': social.color }}
                      whileHover={{ scale: 1.1, y: -5 }}
                      whileTap={{ scale: 0.95 }}
                      initial={{ opacity: 0, y: 20 }}
                      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                      transition={{ delay: 1 + index * 0.1 }}
                      aria-label={social.name}
                    >
                      <i className={social.icon}></i>
                    </motion.a>
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div className="contact__form-container" variants={itemVariants}>
              <form className="contact__form" onSubmit={handleSubmit}>
                <div className="form__group">
                  <input
                    type="text"
                    name="name"
                    className="form__input"
                    placeholder="Your Name *"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                  <label className="form__label">Name</label>
                </div>
                
                <div className="form__group">
                  <input
                    type="email"
                    name="email"
                    className="form__input"
                    placeholder="Your Email *"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                  <label className="form__label">Email</label>
                </div>
                
                <div className="form__group">
                  <input
                    type="text"
                    name="subject"
                    className="form__input"
                    placeholder="Subject *"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                  />
                  <label className="form__label">Subject</label>
                </div>
                
                <div className="form__group form__group--textarea">
                  <textarea
                    name="message"
                    className="form__input form__textarea"
                    placeholder="Your Message *"
                    rows="6"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                  ></textarea>
                  <label className="form__label">Message</label>
                </div>

                <button 
                  type="submit" 
                  className="btn btn--primary btn--block"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane"></i>
                      Send Message
                    </>
                  )}
                </button>

                {submitStatus === 'success' && (
                  <motion.div 
                    className="form__success"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                  >
                    <i className="fas fa-check-circle"></i>
                    Thank you! Your message has been sent successfully.
                  </motion.div>
                )}
              </form>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;